import React from 'react';
import {Button} from '@forecast-it/design-system';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import {useIntl} from 'react-intl';

type Props = {
	/**
	 * The gen AI kill switch. The button keeps its place and its sparkle in the nav and is greyed
	 * and inert, rather than disappearing: hiding is what ineligibility does, greying is what the
	 * kill switch does, and the difference is what tells a user AI was switched off rather than
	 * never bought.
	 */
	disabled?: boolean;
};

const AgentTriggerButton = ({disabled = false}: Props): JSX.Element => {
	const {isOpen, openDrawer, closeDrawer} = useAgentDrawerContext();
	const {formatMessage} = useIntl();

	const askAi = formatMessage({id: 'nova.trigger.ask_ai'});

	// The button is named by its visible text, so voice control users can say "Ask AI", and whether the drawer is
	// open is told by aria-expanded. When gen AI is off the button is natively disabled and can't be focused, so
	// the reason goes in the name, after the visible text, where screen readers always read it.
	return (
		<Button
			emphasis="medium"
			rightIcon="sparkle"
			disabled={disabled}
			onPress={isOpen ? closeDrawer : openDrawer}
			aria-label={disabled ? `${askAi}, ${formatMessage({id: 'gen_ai.disabled_label'})}` : undefined}
			aria-expanded={disabled ? undefined : isOpen}
			data-cy="agent-trigger-button"
		>
			{askAi}
		</Button>
	);
};

export default AgentTriggerButton;
